/**
 * Document upload page - upload documents for fact extraction and verification
 */

import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { documentApi, resultsApi, searchApi } from '../services/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ProvideSourceModal } from '../components/ProvideSourceModal'
import type { DocumentUploadResult, SearchSourcesResult } from '../types'

type UploadMode = 'parse' | 'fact-check' | 'find-sources'
type InputType = 'file' | 'text'

// Maximum text paste size (1MB) to prevent memory issues
const MAX_TEXT_LENGTH = 1024 * 1024
const MAX_TEXT_LENGTH_DISPLAY = '1MB'

/**
 * Deep-link mode (#57): /upload?url=<encoded>[&result=<id>&fact=<key>]
 * opens the provide-source flow pre-scoped to that URL. Agents embed this
 * link in failure messages so a human can hand over the document; on success
 * the fact is re-verified and the user lands back on the result.
 */
const ProvideSourceForUrl = ({
  sourceUrl,
  resultId,
  factKey,
}: {
  sourceUrl: string
  resultId: string | null
  factKey: string | null
}) => {
  const navigate = useNavigate()
  const [reverifying, setReverifying] = useState(false)
  const [status, setStatus] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)

  const returnPath = resultId ? `/results/${resultId}` : '/upload'

  const handleSuccess = async () => {
    if (!resultId || !factKey) {
      setStatus({
        type: 'success',
        message:
          'Source content saved. Future verifications of this URL will use your document automatically.',
      })
      return
    }
    setReverifying(true)
    try {
      await resultsApi.reverifyFact(resultId, factKey)
      navigate(returnPath)
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Document saved, but re-verification failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }. You can re-verify from the result page.`,
      })
    } finally {
      setReverifying(false)
    }
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <h1>Provide Source Document</h1>

      {reverifying && (
        <LoadingSpinner message="Re-verifying the fact with your document..." size="large" />
      )}

      {status && (
        <div
          style={{
            marginTop: '1rem',
            padding: '1rem',
            borderRadius: '4px',
            background: status.type === 'success' ? '#d5f4e6' : '#fadbd8',
            color: status.type === 'success' ? '#27ae60' : '#e74c3c',
          }}
        >
          {status.message}
          {resultId && (
            <div style={{ marginTop: '0.5rem' }}>
              <a href={`/results/${resultId}`} style={{ color: '#3498db' }}>
                Go to the verification result
              </a>
            </div>
          )}
        </div>
      )}

      {!reverifying && !status && (
        <ProvideSourceModal
          sourceUrl={sourceUrl}
          onClose={() => navigate(returnPath)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  )
}

export const DocumentUpload = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [textContent, setTextContent] = useState('')
  const [inputType, setInputType] = useState<InputType>('text')
  const [uploading, setUploading] = useState(false)
  const [uploadMode, setUploadMode] = useState<UploadMode>('fact-check')
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'success' | 'error' | 'info'
    message: string
  } | null>(null)
  const [result, setResult] = useState<DocumentUploadResult | null>(null)
  const [searchResult, setSearchResult] = useState<SearchSourcesResult | null>(null)

  // Deep-link mode: /upload?url=... pre-scopes the provide-source flow (#57)
  const scopedUrl = searchParams.get('url')

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setUploadStatus(null)
      setResult(null)
    }
  }

  const handleUpload = async () => {
    if (inputType === 'file' && !selectedFile) return
    if (inputType === 'text' && !textContent.trim()) return

    // Validate text length to prevent memory issues
    if (inputType === 'text' && textContent.length > MAX_TEXT_LENGTH) {
      setUploadStatus({
        type: 'error',
        message: `Text exceeds maximum size of ${MAX_TEXT_LENGTH_DISPLAY}. Please reduce the content or upload as a file.`,
      })
      return
    }

    setUploading(true)
    setUploadStatus(null)
    setResult(null)
    setSearchResult(null)

    try {
      if (uploadMode === 'find-sources') {
        const claimText = textContent.trim()
        if (!claimText) return
        const response = await searchApi.searchSources(claimText)
        setSearchResult(response)
        setUploadStatus({
          type: 'success',
          message: `Found ${response.candidate_sources.length} candidate sources`,
        })
        setUploading(false)
        return
      } else if (uploadMode === 'fact-check') {
        let response

        if (inputType === 'text') {
          // Create a text file from the pasted content
          const blob = new Blob([textContent], { type: 'text/plain' })
          const file = new File([blob], 'pasted-content.txt', { type: 'text/plain' })
          response = await documentApi.factCheckDocument(file)
        } else {
          // Extract claims and verify them from uploaded file
          response = await documentApi.factCheckDocument(selectedFile!)
        }

        setResult(response)

        if (response.status === 'no_claims') {
          setUploadStatus({
            type: 'info',
            message: response.message || 'No verifiable claims found in content',
          })
        } else {
          setUploadStatus({
            type: 'success',
            message: `Successfully extracted ${response.claims_extracted} claims`,
          })

          // Navigate to results page after 2 seconds
          if (response.display_id) {
            setTimeout(() => {
              navigate(`/results/${response.display_id}`)
            }, 2000)
          }
        }
      } else {
        // Just parse and extract text
        if (inputType === 'text') {
          const blob = new Blob([textContent], { type: 'text/plain' })
          const file = new File([blob], 'pasted-content.txt', { type: 'text/plain' })
          const response = await documentApi.parseDocument(file)
          setResult(response)
          setUploadStatus({
            type: 'success',
            message: 'Successfully parsed pasted content. Document stored for future verification.',
          })
        } else {
          const response = await documentApi.parseDocument(selectedFile!)
          setResult(response)
          setUploadStatus({
            type: 'success',
            message: `Successfully parsed ${selectedFile!.name}. Document stored for future verification.`,
          })
        }
      }

      setSelectedFile(null)
      setTextContent('')
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Processing failed',
      })
    } finally {
      setUploading(false)
    }
  }

  if (scopedUrl) {
    return (
      <ProvideSourceForUrl
        sourceUrl={scopedUrl}
        resultId={searchParams.get('result')}
        factKey={searchParams.get('fact')}
      />
    )
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <h1>Verify Claims</h1>

      <div
        style={{
          background: '#ecf0f1',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
        }}
      >
        <p style={{ marginTop: 0 }}>
          Paste text or upload documents to extract and verify factual claims automatically. The
          system will:
        </p>
        <ul style={{ marginBottom: 0 }}>
          <li>Extract text from your content (pasted text, PDF, or TXT file)</li>
          <li>Use AI to identify verifiable claims with citations</li>
          <li>Automatically verify each claim against its cited source</li>
          <li>Provide detailed validation results with reasoning</li>
        </ul>
      </div>

      {/* Input Type Selection */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          Input Method:
        </label>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="text"
              checked={inputType === 'text'}
              onChange={(e) => setInputType(e.target.value as InputType)}
              style={{ marginRight: '0.5rem' }}
            />
            <span>
              <strong>Paste Text</strong> - Copy and paste content directly
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="file"
              checked={inputType === 'file'}
              onChange={(e) => setInputType(e.target.value as InputType)}
              style={{ marginRight: '0.5rem' }}
            />
            <span>
              <strong>Upload File</strong> - Select PDF or TXT file
            </span>
          </label>
        </div>
      </div>

      {/* Mode Selection */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          Processing Mode:
        </label>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="fact-check"
              checked={uploadMode === 'fact-check'}
              onChange={(e) => setUploadMode(e.target.value as UploadMode)}
              style={{ marginRight: '0.5rem' }}
            />
            <span>
              <strong>Fact Check</strong> - Extract claims and verify them
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="parse"
              checked={uploadMode === 'parse'}
              onChange={(e) => setUploadMode(e.target.value as UploadMode)}
              style={{ marginRight: '0.5rem' }}
            />
            <span>
              <strong>Parse Only</strong> - Extract text and metadata
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              value="find-sources"
              checked={uploadMode === 'find-sources'}
              onChange={(e) => setUploadMode(e.target.value as UploadMode)}
              style={{ marginRight: '0.5rem' }}
            />
            <span>
              <strong>Find Sources</strong> - Search web for sources that support a claim
            </span>
          </label>
        </div>
      </div>

      {/* Input Area */}
      {inputType === 'text' ? (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Paste your content here:
          </label>
          <textarea
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            placeholder={
              uploadMode === 'find-sources'
                ? 'Enter a claim to search for supporting sources (e.g., "The Eiffel Tower is 330 meters tall")...'
                : 'Paste text containing claims with citations (e.g., research papers, articles, reports)...'
            }
            style={{
              width: '100%',
              minHeight: '300px',
              padding: '1rem',
              borderRadius: '8px',
              border: `2px solid ${textContent.length > MAX_TEXT_LENGTH ? '#e74c3c' : '#bdc3c7'}`,
              fontSize: '1rem',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '0.25rem',
              fontSize: '0.85rem',
              color: textContent.length > MAX_TEXT_LENGTH ? '#e74c3c' : '#7f8c8d',
            }}
          >
            {textContent.length > 0 && (
              <span>
                {(textContent.length / 1024).toFixed(1)} KB / {MAX_TEXT_LENGTH_DISPLAY}
                {textContent.length > MAX_TEXT_LENGTH && ' (exceeds limit)'}
              </span>
            )}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
            <button
              onClick={handleUpload}
              disabled={uploading || !textContent.trim()}
              style={{
                background: '#27ae60',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '4px',
                cursor: uploading || !textContent.trim() ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
              }}
            >
              {uploading
                ? 'Processing...'
                : uploadMode === 'fact-check'
                  ? 'Extract & Verify Claims'
                  : uploadMode === 'find-sources'
                    ? 'Search for Sources'
                    : 'Parse Content'}
            </button>
            <button
              onClick={() => setTextContent('')}
              disabled={uploading || !textContent}
              style={{
                background: '#95a5a6',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '4px',
                cursor: uploading || !textContent ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
              }}
            >
              Clear
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            border: '2px dashed #bdc3c7',
            borderRadius: '8px',
            padding: '3rem',
            textAlign: 'center',
            background: 'white',
          }}
        >
          <input
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.txt,.html,.htm,.md,.markdown"
            style={{ display: 'none' }}
            id="file-upload"
          />

          {!selectedFile ? (
            <label htmlFor="file-upload">
              <div
                style={{
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
                <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                  Click to select a document
                </p>
                <p style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                  Supported formats: PDF, TXT, HTML, Markdown
                </p>
              </div>
            </label>
          ) : (
            <div>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
              <p style={{ fontSize: '1.1rem', fontWeight: 500 }}>{selectedFile.name}</p>
              <p style={{ color: '#7f8c8d', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  style={{
                    background: '#27ae60',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '4px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                  }}
                >
                  {uploading
                    ? 'Processing...'
                    : uploadMode === 'fact-check'
                      ? 'Extract & Verify'
                      : uploadMode === 'find-sources'
                        ? 'Search for Sources'
                        : 'Parse Document'}
                </button>
                <button
                  onClick={() => setSelectedFile(null)}
                  disabled={uploading}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '4px',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading Spinner */}
      {uploading && (
        <LoadingSpinner
          message={
            uploadMode === 'fact-check'
              ? 'Extracting and verifying claims... This may take a minute.'
              : uploadMode === 'find-sources'
                ? 'Searching for supporting sources...'
                : 'Parsing document...'
          }
          size="large"
        />
      )}

      {/* Status Message */}
      {uploadStatus && (
        <div
          style={{
            marginTop: '1rem',
            padding: '1rem',
            borderRadius: '4px',
            background:
              uploadStatus.type === 'success'
                ? '#d5f4e6'
                : uploadStatus.type === 'info'
                  ? '#d1ecf1'
                  : '#fadbd8',
            color:
              uploadStatus.type === 'success'
                ? '#27ae60'
                : uploadStatus.type === 'info'
                  ? '#0c5460'
                  : '#e74c3c',
          }}
        >
          {uploadStatus.message}
        </div>
      )}

      {/* Results Preview */}
      {result && uploadMode === 'fact-check' && (result.claims_extracted ?? 0) > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h3>Extracted Claims ({result.claims_extracted ?? 0})</h3>
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '1.5rem',
              border: '1px solid #ddd',
            }}
          >
            {Object.entries(result.verification_results || {})
              .slice(0, 5)
              .map(([key, data]) => (
                <div
                  key={key}
                  style={{
                    borderBottom: '1px solid #eee',
                    paddingBottom: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '1.2rem',
                        color: data.is_valid ? '#27ae60' : '#e74c3c',
                      }}
                    >
                      {data.is_valid ? '✓' : '✗'}
                    </span>
                    <strong>{key}</strong>
                    <span style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>
                      ({((data.validation_score ?? 0) * 100).toFixed(0)}% confidence)
                    </span>
                  </div>
                  <p style={{ margin: '0.5rem 0', color: '#2c3e50' }}>{data.claim}</p>
                  <p style={{ margin: '0.5rem 0', fontSize: '0.85rem', color: '#7f8c8d' }}>
                    Citation:{' '}
                    <a href={data.citation} target="_blank" rel="noopener noreferrer">
                      {data.citation}
                    </a>
                  </p>
                </div>
              ))}
            {(result.claims_extracted ?? 0) > 5 && (
              <p style={{ textAlign: 'center', color: '#7f8c8d', fontSize: '0.9rem' }}>
                ... and {(result.claims_extracted ?? 0) - 5} more claims. Redirecting to full
                results...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Find Sources Results */}
      {searchResult && uploadMode === 'find-sources' && (
        <div style={{ marginTop: '2rem' }}>
          <h3>Source Search Results</h3>
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '1.5rem',
              border: '1px solid #ddd',
            }}
          >
            <p style={{ color: '#555', marginTop: 0 }}>{searchResult.search_summary}</p>

            {/* Auto-verification result */}
            {searchResult.verification_result && (
              <div
                style={{
                  background:
                    searchResult.verification_result.classification === 'SUPPORTS'
                      ? '#e8f5e9'
                      : searchResult.verification_result.classification === 'REFUTES'
                        ? '#ffebee'
                        : '#f5f5f5',
                  padding: '1rem',
                  borderRadius: '6px',
                  marginBottom: '1rem',
                  border: `1px solid ${
                    searchResult.verification_result.classification === 'SUPPORTS'
                      ? '#81c784'
                      : searchResult.verification_result.classification === 'REFUTES'
                        ? '#e57373'
                        : '#bdbdbd'
                  }`,
                }}
              >
                <strong>Auto-verification against top source:</strong>{' '}
                <span
                  style={{
                    color:
                      searchResult.verification_result.classification === 'SUPPORTS'
                        ? '#27ae60'
                        : searchResult.verification_result.classification === 'REFUTES'
                          ? '#e74c3c'
                          : '#95a5a6',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                  }}
                >
                  {searchResult.verification_result.classification}
                </span>
                <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                  ({(searchResult.verification_result.validation_score * 100).toFixed(0)}%
                  confidence)
                </span>
                {searchResult.display_id && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <a href={`/results/${searchResult.display_id}`} style={{ color: '#3498db' }}>
                      View full verification result
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Candidate sources */}
            <h4 style={{ marginBottom: '0.5rem' }}>
              Candidate Sources ({searchResult.candidate_sources.length})
            </h4>
            {searchResult.candidate_sources.map((source, idx) => (
              <div
                key={idx}
                style={{
                  padding: '0.75rem 1rem',
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  marginBottom: '0.5rem',
                  border: '1px solid #e9ecef',
                }}
              >
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#3498db', fontWeight: 500 }}
                >
                  {source.title}
                </a>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                  {source.snippet}
                </p>
                {source.relevance_note && (
                  <p
                    style={{
                      margin: '0.25rem 0 0 0',
                      fontSize: '0.85rem',
                      color: '#7f8c8d',
                      fontStyle: 'italic',
                    }}
                  >
                    {source.relevance_note}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {result && uploadMode === 'parse' && (
        <div style={{ marginTop: '2rem' }}>
          <h3>Document Metadata</h3>
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '1.5rem',
              border: '1px solid #ddd',
            }}
          >
            <p>
              <strong>Document ID:</strong> {result.document_id}
            </p>
            <p>
              <strong>Type:</strong> {result.metadata?.document_type}
            </p>
            <p>
              <strong>Word Count:</strong> {result.metadata?.word_count}
            </p>
            <p>
              <strong>Extraction Quality:</strong> {result.metadata?.extraction_quality}
            </p>
            {result.metadata?.doi && (
              <p>
                <strong>DOI:</strong> {result.metadata.doi}
              </p>
            )}
            {result.metadata?.arxiv_id && (
              <p>
                <strong>arXiv ID:</strong> {result.metadata.arxiv_id}
              </p>
            )}
            <details style={{ marginTop: '1rem' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Preview Text</summary>
              <pre
                style={{
                  background: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '4px',
                  overflow: 'auto',
                  marginTop: '0.5rem',
                }}
              >
                {result.text_preview}
              </pre>
            </details>
          </div>
        </div>
      )}
    </div>
  )
}
